import type { TextFieldProps } from '@mui/material';
import { InputAdornment, TextField } from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import InputLabel from '@components/InputLabel/index';
import InputSubLabel from '@components/InputSubLabel';

import useUniqueId from '@hooks/useUniqueId';

import { EyeImage, TextInputStyle } from './TextInputBox.style';

const messages = defineMessages({
  viewPassword: {
    id: 'textInputBox.viewPassword',
    defaultMessage: 'Toggle view password',
  },
});

interface TextInputBoxProps extends Omit<
  TextFieldProps<'outlined'>,
  'value' | 'variant'
> {
  id?: string;
  label?: string;
  sublabel?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: number | string | null;
  required?: boolean;
  placeholder?: string;
  hasError?: boolean;
  autoComplete?: string;
  type?: React.HTMLInputTypeAttribute;
  width?: string;
  disabled?: boolean;
}

const TextInputBox = React.forwardRef<HTMLInputElement, TextInputBoxProps>(
  (
    {
      id = '',
      label = '',
      sublabel = '',
      required = false,
      type = 'text',
      onChange,
      value = '',
      width,
      placeholder = '',
      hasError = false,
      autoComplete = 'on',
      color,
      helperText,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const { formatMessage } = useIntl();

    const [viewing, setViewing] = useState(false);
    const uniqueId = useUniqueId('input');

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      if (onChange) {
        onChange(event);
      }
    }

    function resolveType() {
      if (type === 'password' && viewing) {
        return 'text';
      }
      return type;
    }

    function toggleView(event: React.MouseEvent<HTMLButtonElement>) {
      event.preventDefault();
      setViewing((viewing) => !viewing);
    }

    return (
      <TextInputStyle width={width}>
        <InputLabel
          inputId={id || uniqueId}
          label={label}
          required={required}
          style={{ color: 'primary.main' }}
        />
        <InputSubLabel label={sublabel} />
        <TextField
          fullWidth
          autoComplete={autoComplete}
          color={hasError ? 'error' : color}
          disabled={disabled}
          error={hasError}
          helperText={helperText}
          data-testid="text-input-box"
          id={id || uniqueId}
          onChange={handleChange}
          placeholder={placeholder}
          ref={ref}
          type={resolveType()}
          value={
            typeof value === 'string' || typeof value === 'number' ? value : ''
          }
          {...props}
          InputProps={{
            ...props.InputProps,
            endAdornment:
              type === 'password' ? (
                <InputAdornment position="end">
                  <EyeImage
                    aria-label={formatMessage(messages.viewPassword)}
                    onClick={toggleView}
                    type="button"
                  >
                    <Icon
                      clickable
                      color="text.secondary"
                      name="eye"
                      size="2x"
                      type="fad"
                    />
                  </EyeImage>
                </InputAdornment>
              ) : (
                props.InputProps?.endAdornment
              ),
          }}
          variant="outlined"
        />
      </TextInputStyle>
    );
  }
);

export default TextInputBox;
